import React, { useEffect, useState } from "react";
import {
  Table, Button, Tag, Space, Typography, Input, Popconfirm,
  message, Select, Avatar, Card, Row, Col, Tooltip, Grid,
} from "antd";
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
  StarOutlined, StarFilled, EyeOutlined,
} from "@ant-design/icons";
import { getAllPosts, getAllAuthors, deletePost, updatePost } from "../utilscopy/firebase";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const categoryColors = {
  technology: "blue", design: "purple", development: "green",
  productivity: "orange", career: "red",
};

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "technology", label: "Technology" },
  { value: "design", label: "Design" },
  { value: "development", label: "Development" },
  { value: "productivity", label: "Productivity" },
  { value: "career", label: "Career" },
];

export default function PostsList({ onEditPost, onNewPost }) {
  const [posts, setPosts] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);

  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  const loadData = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([getAllPosts(), getAllAuthors()]);
      setPosts(p);
      setAuthors(a);
    } catch (e) {
      message.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deletePost(id);
      message.success("Post deleted");
      setPosts((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      message.error("Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleFeatured = async (post) => {
    try {
      await updatePost(post.id, { featured: !post.featured });
      setPosts((prev) =>
        prev.map((p) => p.id === post.id ? { ...p, featured: !p.featured } : p)
      );
      message.success(post.featured ? "Removed from featured" : "Marked as featured");
    } catch (e) {
      message.error("Update failed");
    }
  };

  // TODO: replace with real navigation once a post editor (page or modal) exists.
  const handleEdit = (record) => {
    if (onEditPost) {
      onEditPost(record);
    } else {
      message.info("Post editor isn't wired up yet — pass an onEditPost prop or open an edit modal here.");
    }
  };

  const handleNewPost = () => {
    if (onNewPost) {
      onNewPost();
    } else {
      message.info("Post editor isn't wired up yet — pass an onNewPost prop or open a create modal here.");
    }
  };

  const filtered = posts.filter((p) => {
    const matchSearch =
      search === "" ||
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.excerpt?.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === "all" || p.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const columns = [
    {
      title: "Cover",
      dataIndex: "cover",
      key: "cover",
      render: (src) => (
        <img
          src={src}
          alt="cover"
          style={{ width: 64, height: 44, objectFit: "cover", borderRadius: 8, border: "1px solid #f0f0f0" }}
        />
      ),
      width: 80,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <div>
          <Text
            strong
            style={{ color: "#111", fontSize: 14, cursor: "pointer" }}
            onClick={() => handleEdit(record)}
          >
            {text}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
            {record.excerpt?.slice(0, 60)}...
          </Text>
        </div>
      ),
      ellipsis: true,
      width: 200,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (cat) => (
        <Tag color={categoryColors[cat] || "default"} style={{ borderRadius: 20 }}>
          {cat}
        </Tag>
      ),
      width: 120,
    },
    {
      title: "Author",
      dataIndex: "authorId",
      key: "author",
      render: (authorId) => {
        const author = authors.find((a) => a.id === authorId);
        return author ? (
          <Space>
            <Avatar src={author.avatar} size={22} />
            <Text style={{ fontSize: 13 }}>{author.name}</Text>
          </Space>
        ) : <Text type="secondary">—</Text>;
      },
      width: 150,
    },
    {
      title: "Read Time",
      dataIndex: "readTime",
      key: "readTime",
      render: (v) => <Text type="secondary" style={{ fontSize: 13 }}>{v}</Text>,
      width: 100,
    },
    {
      title: "Featured",
      dataIndex: "featured",
      key: "featured",
      render: (v, record) => (
        <Tooltip title={v ? "Remove from featured" : "Mark as featured"}>
          <Button
            type="text"
            icon={v ? <StarFilled style={{ color: "#f59e0b" }} /> : <StarOutlined style={{ color: "#94a3b8" }} />}
            onClick={() => toggleFeatured(record)}
          />
        </Tooltip>
      ),
      width: 80,
      align: "center",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (d) => <Text type="secondary" style={{ fontSize: 12 }}>{d}</Text>,
      width: 110,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            style={{ borderColor: "#e2e8f0", borderRadius: 8 }}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete post?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={deletingId === record.id}
              style={{ borderRadius: 8 }}
            />
          </Popconfirm>
        </Space>
      ),
      width: 100,
    },
  ];

  return (
    <div>
      {/* Toolbar */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }} align="middle">
        <Col xs={24} md={10} lg={12}>
          <Input
            size="large"
            placeholder="Search posts by title or excerpt..."
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ borderRadius: 10, width: "100%", maxWidth: isMobile ? "100%" : 380 }}
            allowClear
          />
        </Col>
        <Col xs={24} md={7} lg={6}>
          <Select
            size="large"
            value={categoryFilter}
            onChange={setCategoryFilter}
            style={{ width: "100%", borderRadius: 10 }}
            options={CATEGORIES}
          />
        </Col>
        <Col xs={24} md={7} lg={6}>
          <Button
            type="primary"
            size="large"
            block={isMobile}
            icon={<PlusOutlined />}
            style={{ borderRadius: 10, background: "#111", borderColor: "#111", fontWeight: 600 }}
            onClick={handleNewPost}
          >
            New Post
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <Card
        style={{ borderRadius: 16, border: "1px solid #f0f0f0" }}
        bodyStyle={{ padding: 0 }}
        title={
          <Text type="secondary" style={{ fontSize: 14 }}>
            {filtered.length} post{filtered.length !== 1 ? "s" : ""}
            {categoryFilter !== "all" ? ` in ${categoryFilter}` : ""}
            {search ? ` matching "${search}"` : ""}
          </Text>
        }
      >
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 8, showSizeChanger: false }}
          size="middle"
          scroll={{ x: 940 }}
          style={{ borderRadius: "0 0 16px 16px", overflow: "hidden" }}
        />
      </Card>
    </div>
  );
}